import { mapUpdateInto } from "collection-utils";

import {
    TableGlideType,
    TypeSchema,
    ScreenDescriptionKind,
    getTableName,
    areTableNamesEqual,
    findTable,
    getTableRefTableName,
    isSingleRelationType,
} from "./description";
import { AppDescription, AppKind, getAppKind } from "../components/SerializedApp";
import { handlerForComponentKind } from "./handlers";
import { handlerForArrayScreenFormat } from "./array-screens";
import { TableName } from "../database-strings";
import { getScreensUsedByApp } from "./screens-used-by-app";
import { AppDescriptionContext, elevateAppDescriptionContext } from "./components";
import { memoizeFunction } from "../memoize";

type FavoritesPivotNames = ReadonlyMap<TableGlideType, string | true>;

function getAllFavoritesTables(adc: AppDescriptionContext): FavoritesPivotNames {
    const tables = new Map<TableGlideType, string | true>();

    const eadc = elevateAppDescriptionContext(adc);
    if (eadc === undefined) return tables;
    if (getAppKind(eadc.appDescription) !== AppKind.App) return tables;

    const { schema, userProfileTableInfo } = adc;

    const screensUsed = getScreensUsedByApp(eadc, false);

    function update(pivots: FavoritesPivotNames | undefined) {
        if (pivots === undefined) return;

        for (const [t, v] of pivots) {
            mapUpdateInto(tables, t, w => (w === true || w === undefined ? v : w));
        }
    }

    for (const [, screenDesc] of screensUsed) {
        if (screenDesc.kind === ScreenDescriptionKind.Class) {
            for (const desc of screenDesc.components) {
                const handler = handlerForComponentKind(desc.kind);
                update(handler?.getFavoritePivots(desc, screenDesc, schema, userProfileTableInfo));
            }
        } else if (screenDesc.kind === ScreenDescriptionKind.Array) {
            const handler = handlerForArrayScreenFormat(screenDesc.format);
            if (handler !== undefined && isSingleRelationType(screenDesc.type)) {
                const table = findTable(schema, getTableRefTableName(screenDesc.type));
                if (table !== undefined) {
                    update(handler.getFavoritePivots(screenDesc, table));
                }
            }
        }
    }

    return tables;
}

let lastAppDescription: AppDescription | undefined;
let lastSchema: TypeSchema | undefined;
let lastFavoritesPivotNames: FavoritesPivotNames | false | undefined;
let lastFavoritesTableNames: readonly TableName[] | undefined;

export const getAllFavoritesPivotNames = memoizeFunction(
    "getAllFavoritesPivotNames",
    (adc: AppDescriptionContext): FavoritesPivotNames => {
        const { appDescription, schema } = adc;
        if (appDescription === undefined) return new Map();

        if (lastAppDescription === appDescription && lastSchema === schema && lastFavoritesPivotNames !== undefined) {
            if (lastFavoritesPivotNames === false) return new Map();
            return lastFavoritesPivotNames;
        }

        lastAppDescription = appDescription;
        lastSchema = schema;
        lastFavoritesPivotNames = false;
        lastFavoritesTableNames = undefined;

        lastFavoritesPivotNames = getAllFavoritesTables(adc);
        lastFavoritesTableNames = Array.from(lastFavoritesPivotNames.keys()).map(getTableName);

        return lastFavoritesPivotNames;
    }
);

export function getAllFavoritesTableNames(adc: AppDescriptionContext | undefined): readonly TableName[] {
    if (adc?.appDescription === undefined) return [];

    getAllFavoritesPivotNames(adc);

    return lastFavoritesTableNames ?? [];
}

export function hasTableFavorites(tableName: TableName, adc: AppDescriptionContext): boolean {
    const favoritesTables = getAllFavoritesTableNames(adc);
    return favoritesTables.some(n => areTableNamesEqual(n, tableName));
}
